import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Trophy, Clock, MapPin, ArrowUpRight, Instagram, Facebook, Newspaper } from "lucide-react";
import { useLang } from "../../i18n";
import { Reveal, MaskLine, fmtDate, fmtTime, fmtDay } from "../../lib/motion.jsx";
import { getSportOverview, getSportCompetitions, getSportStandings, getTrainings, getSocial, getPress } from "../../lib/api";
import { TikTokEmbed } from "../../components/TikTokEmbed";
import { MatchRow, StandingsTable } from "./SportBits";

const HEROES = {
  futsal: "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/2go6mpan_667769689_122200312976371548_4558226131540071981_n.webp",
  football: "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/lb9oi9lm_image00001%20%281%29.webp",
};

const Label = ({ children }) => (
  <span className="text-[11px] uppercase tracking-[0.3em] text-[#CCFF00]">{children}</span>
);

export default function SportHome({ sport }) {
  const { t, lang } = useLang();
  const [ov, setOv] = useState(null);
  const [comps, setComps] = useState([]);
  const [compId, setCompId] = useState("");
  const [standings, setStandings] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [press, setPress] = useState([]);
  const [social, setSocial] = useState([]);

  const base = sport === "futsal" ? "/fc-inkad/futsal" : "/fc-inkad/veldvoetbal";
  const sportLabel = t(sport === "futsal" ? "nav_futsal" : "nav_veldvoetbal");

  useEffect(() => {
    setOv(null);
    getSportOverview(sport).then((o) => { setOv(o); setStandings(o.standings || []); setCompId(o.competition_id || ""); });
    getSportCompetitions(sport).then(setComps);
    getTrainings("fc-inkad", sport).then(setTrainings);
    getPress().then(setPress);
    getSocial("fc-inkad").then(setSocial);
  }, [sport]);

  const changeComp = (cid) => {
    setCompId(cid);
    getSportStandings(sport, cid).then(setStandings);
  };

  const next = ov?.next_match;
  const last = ov?.last_result;
  const live = ov?.live || [];

  return (
    <div className="fc-theme" data-testid={`sport-home-${sport}`}>
      {/* HERO */}
      <section className="relative h-[62vh] min-h-[440px] overflow-hidden">
        <img src={HEROES[sport]} alt={sportLabel} style={{ objectPosition: "center 28%" }} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]/20" />
        <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-12">
          <span className="mb-3 text-[11px] uppercase tracking-[0.3em] text-[#CCFF00]">FC Inkad · {ov?.club?.name || "Diegem"}</span>
          <h1 className="font-anton text-6xl md:text-8xl uppercase leading-none">
            <MaskLine>{sportLabel}</MaskLine>
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-white/60">
            <span className="flex items-center gap-2">
              <Radio size={14} className="text-[#CCFF00]" /> {t("officialData")}
            </span>
            {ov?.last_synced_at && (
              <span data-testid="sport-last-updated" className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#CCFF00] animate-pulse" />
                {t("lastUpdated")}: {fmtTime(ov.last_synced_at)}
              </span>
            )}
          </div>
          <div className="mt-6">
            <Link to="/fc-inkad/inschrijven" data-testid="sport-hero-register"
              className="inline-flex items-center gap-2 rounded-sm bg-[#CCFF00] px-6 py-3 text-sm font-extrabold uppercase text-black transition-transform duration-150 hover:scale-105">
              {t("nav_inschrijven")} <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-14 space-y-20">
        {/* LIVE */}
        {live.length > 0 && (
          <section data-testid="sport-live-section">
            <h2 className="font-anton text-3xl uppercase mb-6 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 bg-red-500 px-3 py-1 text-sm font-extrabold text-white">● {t("stLive")}</span>
            </h2>
            <div className="space-y-3">{live.map((m) => <MatchRow key={m.external_id} m={m} t={t} lang={lang} />)}</div>
          </section>
        )}

        {/* VOLGENDE WEDSTRIJD + LAATSTE RESULTAAT */}
        <section className="grid gap-4 lg:grid-cols-2">
          <Reveal>
            <div className="h-full border border-[#CCFF00]/30 bg-gradient-to-br from-[#141414] to-[#0A0A0A] p-8" data-testid="sport-next-match">
              <Label>{t("nextMatch")}</Label>
              {next ? (
                <>
                  <div className="mt-6 flex items-center justify-between gap-2">
                    <div className="flex-1 text-center">
                      {next.home_logo && <img src={next.home_logo} alt="" className="mx-auto mb-2 h-12 w-12 object-contain" />}
                      <div className="font-anton text-xl md:text-2xl uppercase">{next.home_team}</div>
                    </div>
                    <div className="px-4 font-mono2 text-3xl text-white/30">VS</div>
                    <div className="flex-1 text-center">
                      {next.away_logo && <img src={next.away_logo} alt="" className="mx-auto mb-2 h-12 w-12 object-contain" />}
                      <div className="font-anton text-xl md:text-2xl uppercase">{next.away_team}</div>
                    </div>
                  </div>
                  <div className="mt-8 space-y-2 text-sm text-white/60">
                    <div className="flex items-center gap-2"><Clock size={14} className="text-[#CCFF00]" /> {fmtDay(next.datetime, lang)} {fmtDate(next.datetime, lang)} · {next.time}</div>
                    {next.venue && <div className="flex items-center gap-2"><MapPin size={14} className="text-[#CCFF00]" /> {next.venue}</div>}
                    <div className="flex items-center gap-2"><Trophy size={14} className="text-[#CCFF00]" /> {next.competition_name}</div>
                  </div>
                </>
              ) : (
                <p className="mt-6 text-white/40">{t("noMatches")}</p>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full border border-white/10 bg-[#141414] p-8" data-testid="sport-last-result">
              <Label>{t("lastResult")}</Label>
              {last ? (
                <>
                  <div className="mt-6 flex items-center justify-between gap-2">
                    <div className="flex-1 text-center">
                      {last.home_logo && <img src={last.home_logo} alt="" className="mx-auto mb-2 h-12 w-12 object-contain" />}
                      <div className="font-anton text-xl md:text-2xl uppercase">{last.home_team}</div>
                    </div>
                    <div className="px-4 text-center">
                      <span className="font-mono2 text-4xl font-bold text-[#CCFF00]">{last.home_score ?? "–"}–{last.away_score ?? "–"}</span>
                      {last.home_pens != null && <div className="text-xs text-white/40">pen. {last.home_pens}–{last.away_pens}</div>}
                      {last.status === "forfeit" && <div className="mt-1 text-[10px] uppercase text-red-400">{t("stForfeit")}</div>}
                    </div>
                    <div className="flex-1 text-center">
                      {last.away_logo && <img src={last.away_logo} alt="" className="mx-auto mb-2 h-12 w-12 object-contain" />}
                      <div className="font-anton text-xl md:text-2xl uppercase">{last.away_team}</div>
                    </div>
                  </div>
                  <div className="mt-8 space-y-2 text-sm text-white/60">
                    <div className="flex items-center gap-2"><Clock size={14} className="text-[#CCFF00]" /> {fmtDay(last.datetime, lang)} {fmtDate(last.datetime, lang)}</div>
                    <div className="flex items-center gap-2"><Trophy size={14} className="text-[#CCFF00]" /> {last.competition_name}</div>
                  </div>
                </>
              ) : (
                <p className="mt-6 text-white/40">{t("noMatches")}</p>
              )}
            </div>
          </Reveal>
        </section>

        {/* STAND */}
        <section data-testid="sport-standings-section">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Trophy size={22} className="text-[#CCFF00]" />
              <h2 className="font-anton text-3xl uppercase">{t("standings")}</h2>
            </div>
            {comps.length > 1 && (
              <select value={compId} onChange={(e) => changeComp(e.target.value)} data-testid="sport-competition-select"
                className="border border-white/15 bg-[#141414] px-3 py-2 text-sm text-white/80 outline-none">
                {comps.map((c) => <option key={c.external_id} value={c.external_id}>{c.name}</option>)}
              </select>
            )}
          </div>
          {standings.length > 0 ? (
            <Reveal><StandingsTable rows={standings} t={t} /></Reveal>
          ) : (
            <p className="text-white/40">—</p>
          )}
        </section>

        {/* KOMENDE WEDSTRIJDEN + RESULTATEN */}
        <section className="grid gap-10 lg:grid-cols-2">
          <div data-testid="sport-upcoming-list">
            <h2 className="font-anton text-3xl uppercase mb-6">{t("upcoming")}</h2>
            <div className="space-y-3">
              {(ov?.upcoming || []).map((m) => <MatchRow key={m.external_id} m={m} t={t} lang={lang} />)}
              {ov && (ov.upcoming || []).length === 0 && <p className="text-white/40 text-sm">{t("noMatches")}</p>}
            </div>
          </div>
          <div data-testid="sport-results-list">
            <h2 className="font-anton text-3xl uppercase mb-6">{t("recentResults")}</h2>
            <div className="space-y-3">
              {(ov?.recent_results || []).map((m) => <MatchRow key={m.external_id} m={m} t={t} lang={lang} />)}
              {ov && (ov.recent_results || []).length === 0 && <p className="text-white/40 text-sm">{t("noMatches")}</p>}
            </div>
          </div>
        </section>

        <Reveal>
          <Link to={`${base}/wedstrijden`} data-testid="sport-all-matches-link"
            className="group flex items-center justify-between border border-white/10 bg-[#141414] p-6 hover:border-[#CCFF00] transition-colors">
            <span className="font-anton text-2xl uppercase">{t("allMatches")}</span>
            <ArrowUpRight className="text-[#CCFF00] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </Reveal>

        {/* TRAININGEN */}
        {trainings.length > 0 && (
          <section>
            <h2 className="font-anton text-3xl uppercase mb-6">{t("trainings")}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {trainings.map((tr, i) => (
                <Reveal key={tr.id} delay={i * 0.06}>
                  <div className="border-l-2 border-[#CCFF00] bg-[#141414] p-5">
                    <div className="text-[10px] uppercase tracking-widest text-[#CCFF00]">{tr.day}</div>
                    <div className="mt-2 font-bold">{tr.team}</div>
                    <div className="mt-1 font-mono2 text-sm text-white/60">{tr.time}</div>
                    <div className="mt-1 text-xs text-white/40">{tr.location}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* NIEUWS — Het Nieuwsblad */}
        <section data-testid="sport-press-section">
          <h2 className="font-anton text-3xl uppercase mb-6">{t("clubNews")}</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {press.slice(0, 6).map((n, i) => (
              <Reveal key={n.id} delay={i * 0.06}>
                <a href={n.url} target="_blank" rel="noreferrer" data-testid="sport-press-card"
                  className="group flex h-full items-start justify-between gap-4 border border-white/10 bg-[#141414] p-6 hover:border-[#CCFF00]/50 transition-colors">
                  <div>
                    <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#CCFF00]">
                      <Newspaper size={12} /> {n.source}
                      {n.date && <span className="text-white/40">· {fmtDate(n.date, lang)}</span>}
                    </div>
                    <h3 className="font-bold text-lg leading-snug transition-colors group-hover:text-[#CCFF00]">{n.title}</h3>
                  </div>
                  <ArrowUpRight size={18} className="shrink-0 text-white/30 transition-all group-hover:text-[#CCFF00] group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </Reveal>
            ))}
          </div>
        </section>

        {/* SOCIAL */}
        <section>
          <h2 className="font-anton text-3xl uppercase mb-6">{t("social")}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {social.slice(0, 6).map((s, i) => (
              <Reveal key={s.id} delay={i * 0.06}>
                <a href={s.permalink || undefined} target={s.permalink ? "_blank" : undefined} rel="noreferrer"
                  className="group relative block overflow-hidden border border-white/10" data-testid="fc-social-card">
                  <div className="aspect-square overflow-hidden bg-[#141414]">
                    {s.image && <img src={s.image} alt={s.handle} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black to-transparent p-4">
                    <div className="mb-1 flex items-center gap-2 text-xs text-[#CCFF00]">
                      {s.platform === "instagram" ? <Instagram size={13} /> : <Facebook size={13} />} {s.handle}
                      {s.date && <span className="text-white/40">· {fmtDate(s.date, lang)}</span>}
                    </div>
                    <p className="line-clamp-3 text-sm text-white/90">{s.text}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </section>

        {/* TIKTOK */}
        <section data-testid="sport-tiktok-section">
          <h2 className="font-anton text-3xl uppercase mb-6">TikTok</h2>
          <Reveal>
            <div className="border border-white/10 bg-[#141414] p-6 md:p-8">
              <div className="bg-white p-2">
                <TikTokEmbed dark />
              </div>
              <div className="mt-6 text-center">
                <a href="https://www.tiktok.com/@inkad.diegem" target="_blank" rel="noreferrer" data-testid="sport-tiktok-follow"
                  className="inline-flex items-center gap-2 rounded-sm bg-[#CCFF00] px-6 py-3 text-sm font-extrabold uppercase text-black transition-transform duration-150 hover:scale-105">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" /></svg>
                  {t("followTiktok")}
                </a>
              </div>
            </div>
          </Reveal>
        </section>

        {/* INSCHRIJVEN */}
        <Reveal>
          <section className="border border-[#CCFF00]/30 bg-gradient-to-br from-[#141414] to-[#0A0A0A] p-10 text-center md:p-16" data-testid="sport-join">
            <h2 className="font-anton text-4xl md:text-6xl uppercase">{t("joinClub")}</h2>
            <Link to="/fc-inkad/inschrijven" className="mt-8 inline-flex items-center gap-2 rounded-sm bg-[#CCFF00] px-8 py-4 font-extrabold uppercase text-black transition-transform duration-150 hover:scale-105">
              {t("nav_inschrijven")} <ArrowUpRight size={18} />
            </Link>
          </section>
        </Reveal>
      </div>
    </div>
  );
}
